import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { type ReactNode, useState } from "react";
import { CompactTable } from "../../../shared/data/compact-table";
import { LandingBubble } from "../../../shared/messaging/landing-bubble";
import {
	landingChatAvatarShellClass,
	landingChatBubblePaddingClass,
	landingChatBubbleRadiusClass,
	landingChatBubbleShellClass,
	landingChatBubbleTextClass,
} from "../../../shared/messaging/landing-bubble-styles";
import { LandingMessage, LandingMessageAvatar, LandingMessageContent } from "../../../shared/messaging/landing-message";
import { ChatAvatar } from "./chat-avatar";
import { CHAT_BOX_GROW_EASE, CHAT_BOX_GROW_S } from "./chat-box-height";
import type { TChatMessageRole, TChatPlaybackMessage, TChatTableContent } from "./chat-playback";
import { ChatTypingIndicator } from "./chat-typing-indicator";
import { StreamingText } from "./streaming-text";

const MESSAGE_GROW_HIDDEN = { height: 0, opacity: 0 };
const MESSAGE_GROW_SHOWN = { height: "auto", opacity: 1 };
const MESSAGE_GROW_TRANSITION = {
	height: { duration: CHAT_BOX_GROW_S, ease: CHAT_BOX_GROW_EASE },
	opacity: { duration: 0.35, delay: 0.12, ease: CHAT_BOX_GROW_EASE },
} as const;

function GrowingMessage({ animate, children }: { animate: boolean; children: ReactNode }) {
	const [settled, setSettled] = useState(!animate);

	return (
		<motion.div
			initial={animate ? MESSAGE_GROW_HIDDEN : false}
			animate={MESSAGE_GROW_SHOWN}
			transition={MESSAGE_GROW_TRANSITION}
			onAnimationComplete={() => setSettled(true)}
			className={settled ? undefined : "overflow-hidden"}
			style={settled ? { height: "auto", overflow: "visible" } : undefined}
		>
			<div>{children}</div>
		</motion.div>
	);
}

type TChatMessageProps = {
	role: TChatMessageRole;
	messages: readonly TChatPlaybackMessage[];
	/** Stream text character-by-character (false when the scene reveals everything at once). */
	animateText: boolean;
	/** Message currently being typed — its body renders as the typing label so the turn and avatar stay mounted. */
	typingMessageId?: string;
};

function isTableContent(content: TChatPlaybackMessage["content"]): content is TChatTableContent {
	return typeof content === "object" && content !== null && "columns" in content;
}

function renderMessageBody(message: TChatPlaybackMessage, animateText: boolean, isTyping: boolean): ReactNode {
	if (isTyping) {
		return <ChatTypingIndicator />;
	}

	if (message.type === "table" && isTableContent(message.content)) {
		const table = message.content;
		return (
			<div className="mt-3.5 max-md:mt-3">
				<CompactTable columns={table.columns} rows={table.rows} rowKey={table.rowKey} />
			</div>
		);
	}

	if (message.stream) {
		return (
			<StreamingText
				text={String(message.content)}
				animate={animateText}
				className={landingChatBubbleTextClass(message.role)}
			/>
		);
	}

	return <p className={landingChatBubbleTextClass(message.role)}>{String(message.content)}</p>;
}

export function ChatMessage({ role, messages, animateText, typingMessageId }: TChatMessageProps) {
	if (messages.length === 0) return null;

	const hideAvatar = messages.every((message) => message.hideAvatar);
	const typingOnly = messages.every((message) => message.id === typingMessageId);

	const avatar = (
		<LandingMessageAvatar className={landingChatAvatarShellClass}>
			<ChatAvatar messageRole={role} />
		</LandingMessageAvatar>
	);

	const content = (
		<LandingMessageContent
			className={cn("w-auto", {
				"min-w-0 flex-1": role === "assistant",
			})}
		>
			{typingOnly ? (
				<ChatTypingIndicator />
			) : (
				<div
					className={cn(landingChatBubbleRadiusClass, {
						"min-w-0 overflow-hidden": role === "assistant",
					})}
					aria-live={role === "assistant" ? "polite" : undefined}
				>
					<LandingBubble
						className={cn({
							"w-full max-w-full": role === "assistant",
						})}
						contentClassName={cn(
							landingChatBubbleRadiusClass,
							landingChatBubbleShellClass(role),
							landingChatBubblePaddingClass(role),
						)}
					>
						{messages.map((message) => (
							<GrowingMessage key={message.id} animate={animateText}>
								{renderMessageBody(message, animateText, message.id === typingMessageId)}
							</GrowingMessage>
						))}
					</LandingBubble>
				</div>
			)}
		</LandingMessageContent>
	);

	return (
		<LandingMessage
			align={role === "user" ? "end" : "start"}
			className={cn("items-start max-w-none gap-3", {
				"!flex-row w-auto self-end": role === "user",
				"flex-row w-full": role === "assistant",
				"items-center": typingOnly,
			})}
		>
			{role === "user" ? (
				<>
					{content}
					{hideAvatar ? null : avatar}
				</>
			) : (
				<>
					{hideAvatar ? null : avatar}
					{content}
				</>
			)}
		</LandingMessage>
	);
}
